import type { OnSuccessObj, SiteVerifyRequest, SiteVerifyRequest_Cust } from '../server/CenPOS';
import { PUBLIC_CENPOS_USE_PROD_CREDS, PUBLIC_ENVIRONMENT, PUBLIC_DOMAIN, PUBLIC_CENPOS_URL_SUBDOMAIN, PUBLIC_CENPOS_URL_SUBDOMAIN_PROD } from '$env/static/public';
import { REST } from '$lib/client/conn';
import { errorModal, eraseDealer } from '$lib/client/store';
import { get } from 'svelte/store';
import { DateTime } from 'luxon';
import { amount, transactionType, address, USE_CVV, _showInvalidInformationError, _showSystemError, cust_id, _CenPOSEl, onLoaded, onLoading } from './components/CenPOS.svelte';
import { KTIForm } from './form';
import { browser } from '$app/environment';

const use_prod = PUBLIC_CENPOS_USE_PROD_CREDS === 'true' || (PUBLIC_ENVIRONMENT !== 'dev' && PUBLIC_ENVIRONMENT !== 'uat');
const subdomain = use_prod ? PUBLIC_CENPOS_URL_SUBDOMAIN_PROD : PUBLIC_CENPOS_URL_SUBDOMAIN;

//let Porthole: any;

// const onResponse_Example = {
//     "data": {
//         "callbacksuccess": true, "transaction": {
//             "json": true, "Result": 0, "Message": "Approved", "CardType": "VISA", "ProtectedCardNumber": "************1111",
//             "RecurringSaleTokenId": "CRYPTO%eyJDcnlwdG8iOiI4dEpsQnZXbSs5XC9Hd2dDU2QwRThjSVwvTDdnd093Z0wrMGROTXNraWY2U1BPWEJGT2ZLRGlpOXgrcGVsT1pPMTVXU0RxVnFlVWxVMUo1RnpKXC9RQUFBQUFBQUFBQUFBQUFBQUFBQUFBPSIsIlNhbHQiOiJmWVpKbmdTajM0ZXBIRVh4VkxQcUpxZytZN3pGXC80SHJTUGhmYzZcL0pNV3VKbk5uUWUrWlJnN1gwMlFXVmFuQXpsakZNMlhaOHFiQWF1YlR5MkxZUkVRPT0iLCJTZXNzaW9uIjoiTnprd01EQTNOVGMwZkRZek9EWXpPVEEzTnprMU1UZ3pOekl4T0E9PSIsIlZlcnNpb24iOiJBRVMgSE1BQyAwLjMifQ==", "NameonCard": "255", "CardExpirationDate": "1225", "CustomerEmail": null, "CustomerZipCode": null, "CustomerAddress": null, "CustomerCode": "C315738", "otp3ds": ""
//         }
//     },
//     "sourceOrigin": "https://webstaging.cenpos.net", "targetOrigin": "*", "sourceWindowName": "cenposPayIFrameId162741", "targetWindowName": ""
// }

// const UseCryptoResponse_Example = {
//     "Result": 0,
//     "Message": "Approved",
//     "AccountBalanceAmount": null,
//     "AutorizationNumber": "708028",
//     "ReferenceNumber": "2203386552",
//     "TraceNumber": null,
//     "Amount": "0.01",
//     "CardType": "VISA",
//     "Discount": "0",
//     "DiscountAmount": "0",
//     "OriginalAmount": "0.01",
//     "ParameterValidationResultList": [
//         {
//             "ExtensionData": {},
//             "Name": "CVV",
//             "Result": " Present;Not Match"
//         },
//         {
//             "ExtensionData": {},
//             "Name": "Billing Address",
//             "Result": " Present;Not Match (1)"
//         },
//         {
//             "ExtensionData": {},
//             "Name": "Zip Code",
//             "Result": " Present;Not Match (1)"
//         }
//     ],
//     "PartiallyAuthorizedAmount": null,
//     "Surcharge": "0",
//     "SurchargeAmount": "0.00",
//     "NetAmount": null,
//     "RecurringTokenId": null,
//     "ProtectedCardNumber": null,
//     "ExpirationCard": "",
//     "ProcessedTransactionType": null,
//     "LastCardNumber": null,
//     "InvoiceNumber": "A6UJ9A001KJU",
//     "NameOnCard": "",
//     "ACSUrl": "",
//     "Payload": null,
//     "TransactionID": null,
//     "View3D": "",
//     "ViewSurcharge": "",
//     "CustomerCode": "A6UJ9A001KJU"
// }

export interface SimpleWebPayOptions {
    url: string;
    params?: string;
    width: string;
    height: string;
    success?: Function;
    cancel?: Function;
    opencomplete?: Function;
    sessionToken?: boolean;
}

// export interface ResponsePayload {
//     CardExpirationDate: string
//     CardType: string
//     CustomerAddress: string
//     CustomerCode: string
//     CustomerEmail: string
//     CustomerZipCode: string
//     Message: string
//     NameonCard: string
//     ProtectedCardNumber: string
//     RecurringSaleTokenId: string
//     Result: number
//     json: boolean
//     otp3ds: string
// }

export interface ParameterValidationResult {
    ExtensionData: {}
    Name: 'CVV' | 'Billing Address' | 'Zip Code' | string
    Result: string
}

export interface UseCryptoResponse {
    CustomerCode?: string,
    AccountBalanceAmount: string | null,
    Amount: string,
    Result: number,
    ACSUrl: any
    AutorizationNumber: any
    CardType: any | 'VISA'
    Discount: any
    DiscountAmount: any
    ExpirationCard: any
    InvoiceNumber: any
    LastCardNumber: any
    Message: any
    NameOnCard: any
    OriginalAmount: any
    ParameterValidationResultList: ParameterValidationResult[]
    PartiallyAuthorizedAmount: any
    Payload: any
    ProcessedTransactionType: any
    ProtectedCardNumber: any
    RecurringTokenId: any
    ReferenceNumber: any
    Surcharge: any
    SurchargeAmount: any
    TraceNumber: any
    TransactionID: any
    View3D: any
    ViewSurcharge: any
}

const CenPOSResponseTypes: { [code: string]: CenPOSResponses } = {
    // Approved
    '0': 'Approved',

    // Declined -- Commented out because if not defined then default to declined
    //   '1': 'Declined', // Decline transaction
    //   '2': 'Declined', // Duplicated transaction
    //   '6': 'Declined', // Rejected
    //   '8': 'Declined', // Processing has timed out
    //   '10': 'Declined', // Late response from issuer
    //   '17': 'Declined', // Settlement in progress
    //   '19': 'Declined', // Rejected by Risk Parameters
    //   '278': 'Declined', // The card country is blocked for this merchant
    //   '287': 'Declined', // Transaction Exceeds Merchant Parameter Limits
    //   '293': 'Declined', // Transaction rejected by merchant configuration
    //   '326': 'Declined', // Transaction not supported by the merchant
    //   '382': 'Declined', // The authorization is expired
    //   '383': 'Declined', // Amount exceeds the merchant limit
    //   '384': 'Declined', // Daily amount limit reached
    //   '385': 'Declined', // Daily limit of transactions reached
    //   '396': 'Declined', // Transaction not supported by card brand
    //   '397': 'Declined', // Rejected by decline restriction
    //   '398': 'Declined', // Not Supported. Card Only Allows Fuel Products
    //   '399': 'Declined', // Call Issuer
    //   '400': 'Declined', // Card Approval Pending
    //   '555': 'Declined', // Previous transaction pending for resolution
    //   '996': 'Declined', // System on Maintenance (CenPOS side)
    //   '997': 'Declined', // Configuration error (CenPOS side)
    //   '998': 'Declined', // Unable to Process (CenPOS side)
    //   '999': 'Declined', // Unable to Process (CenPOS side)
    //   '2017': 'Declined', // Unauthorized

    // Invalid information
    // Card or transaction data issues
    '3': 'Invalid information',   // Commercial card
    '4': 'Invalid information',   // Debit card
    '7': 'Invalid information',   // Original Transaction not found
    '9': 'Invalid information',   // Invalid entry mode
    '11': 'Invalid information',  // Invalid original transaction status
    '12': 'Invalid information',  // Debit and Commercial card
    '13': 'Invalid information',  // The invoice number is invalid.
    '14': 'Invalid information',  // Ebt Card
    '15': 'Invalid information',  // Reversal not needed
    '16': 'Invalid information',  // Reversal not supported
    '18': 'Invalid information',  // Is Fleet
    '21': 'Invalid information',  // Is 3D Secure Transaction
    '100': 'Invalid information', // Service Not Allowed
    '101': 'Invalid information', // Invalid user
    '102': 'Invalid information', // Insufficient privileges
    '103': 'Invalid information', // Blocked user
    '104': 'Invalid information', // Inactive user
    '105': 'Invalid information', // User must change password
    '106': 'Invalid information', // Password doesn't meet strength policy requirements
    '107': 'Invalid information', // Password doesn't meet history policy requirements
    '108': 'Invalid information', // New password must be different to old password
    '109': 'Invalid information', // User already exists
    '111': 'Invalid information', // User cannot delete himself
    '112': 'Invalid information', // Master merchant user cannot be modified
    '114': 'Invalid information', // The merchant is not configured to send emails.
    '119': 'Invalid information', // RedirectUrl is required
    '120': 'Invalid information', // Google AuthCode is required
    '201': 'Invalid information', // Invalid argument
    '202': 'Invalid information', // Null argument
    '210': 'Invalid information', // Invalid brand
    '211': 'Invalid information', // Invalid card number
    '212': 'Invalid information', // Invalid country
    '214': 'Invalid information', // Invalid merchant
    '215': 'Invalid information', // Invalid merchant user
    '216': 'Invalid information', // Invalid receipt type
    '217': 'Invalid information', // Invalid reseller
    '218': 'Invalid information', // Invalid Host
    '219': 'Invalid information', // Invalid state
    '220': 'Invalid information', // Invalid time zone
    '221': 'Invalid information', // Invalid transaction type
    '222': 'Invalid information', // Voids are not supported on debit transactions
    '223': 'Invalid information', // Required host merchant data not received
    '224': 'Invalid information', // Alert email is already in use
    '225': 'Invalid information', // Parameter is already in use for merchant user
    '226': 'Invalid information', // Hierarchy role parameter already in use
    '227': 'Invalid information', // Invalid email format
    '228': 'Invalid information', // Invalid merchant invoice option
    '229': 'Invalid information', // Invalid user invoice option
    '230': 'Invalid information', // Invalid auto close batch hour
    '231': 'Invalid information', // Invalid tax amount
    '244': 'Invalid information', // Parameter is already in use for merchant
    '245': 'Invalid information', // Invalid parameter
    '248': 'Invalid information', // Processor payment settings XML invalid format
    '249': 'Invalid information', // Message format error
    '250': 'Invalid information', // Invalid amount
    '251': 'Invalid information', // Invalid bin
    '252': 'Invalid information', // Invalid card expiration date
    '253': 'Invalid information', // Invalid configuration of the host
    '254': 'Invalid information', // Invalid reference number
    '255': 'Invalid information', // Invalid tax range
    '256': 'Invalid information', // The Ebpp invoice id is required
    '257': 'Invalid information', // Invalid card verification number
    '258': 'Invalid information', // Communication channel is required
    '259': 'Invalid information', // Invalid last card four digits
    '260': 'Invalid information', // Invalid reseller type
    '264': 'Invalid information', // Prepaid Cards not allowed by the merchant
    '265': 'Invalid information', // Invalid industry
    '270': 'Invalid information', // Check Host Bank Already Exists
    '271': 'Invalid information', // Message format error
    '272': 'Invalid information', // Invalid Cash Back Amount
    '273': 'Invalid information', // Hierarchy Role in use
    '274': 'Invalid information', // Inactive account
    '275': 'Invalid information', // Account already active
    '276': 'Invalid information', // Invalid Level 3 Data
    '277': 'Invalid information', // HierarchyRole is Missing Over Linked Merchants
    '279': 'Invalid information', // Entry mode invalid for this device
    '280': 'Invalid information', // Cash Drawer not opened
    '281': 'Invalid information', // Cash Drawer already opened
    '284': 'Invalid information', // Invalid Invoice Detail
    '288': 'Invalid information', // Invalid Currency Code
    '289': 'Invalid information', // No Host Merchant supports the currency
    '300': 'Invalid information', // Must inform the authorization number
    '301': 'Invalid information', // Must inform the billing address
    '302': 'Invalid information', // Must inform the customer code
    '303': 'Invalid information', // Must inform PIN
    '304': 'Invalid information', // Must inform the reference number
    '305': 'Invalid information', // Must inform the signature
    '306': 'Invalid information', // Must inform the transaction type
    '307': 'Invalid information', // Must inform the zip code
    '308': 'Invalid information', // Must Inform Tax and Customer Code
    '309': 'Invalid information', // Must inform invoice number
    '310': 'Invalid information', // Must inform name on card
    '311': 'Invalid information', // Must inform card verification number
    '312': 'Invalid information', // Must inform magnetic data
    '313': 'Invalid information', // Must inform name on check
    '314': 'Invalid information', // Must inform key serial number
    '315': 'Invalid information', // Must inform card last four digits
    '316': 'Invalid information', // Must inform check issue date
    '317': 'Invalid information', // Must inform additional data
    '318': 'Invalid information', // Must inform card number
    '319': 'Invalid information', // Must inform card expiration date
    '320': 'Invalid information', // Must inform amount
    '323': 'Invalid information', // Must inform the customer email
    '327': 'Invalid information', // Invalid original transaction type
    '328': 'Invalid information', // No Host merchant found
    '329': 'Invalid information', // Duplicated force transaction
    '330': 'Invalid information', // Duplicated express checkout payment transaction
    '335': 'Invalid information', // Error applying discount
    '336': 'Invalid information', // Check number is required
    '337': 'Invalid information', // Check number is invalid
    '338': 'Invalid information', // Invalid account type
    '339': 'Invalid information', // Check type is invalid
    '340': 'Invalid information', // Invalid issue date
    '343': 'Invalid information', // ABA number is required
    '344': 'Invalid information', // ABA number is invalid
    '345': 'Invalid information', // Account number is required
    '346': 'Invalid information', // Account number is invalid
    '347': 'Invalid information', // Check type is required
    '348': 'Invalid information', // Check image missing
    '349': 'Invalid information', // Transaction processing is pending
    '350': 'Invalid information', // Transaction has been processed
    '351': 'Invalid information', // Transaction will not be processed
    '352': 'Invalid information', // Merchant setup is invalid
    '355': 'Invalid information', // MICR is invalid
    '356': 'Invalid information', // Driver's license number is invalid
    '357': 'Invalid information', // Phone is required
    '358': 'Invalid information', // State of the customer is required
    '359': 'Invalid information', // Invalid check image
    '360': 'Invalid information', // City is required
    '361': 'Invalid information', // Check images are required
    '370': 'Invalid information', // Required Driver's Number in Validation transactions
    '371': 'Invalid information', // Method not supported by this version
    '372': 'Invalid information', // ABA file does not exist
    '373': 'Invalid information', // Error extracting ABA zip file
    '374': 'Invalid information', // Invalid ABA refresh file
    '375': 'Invalid information', // ABA refresh process already running
    '376': 'Invalid information', // Unknown error processing ABA file
    '377': 'Invalid information', // Failed Reloading ABAs
    '378': 'Invalid information', // Transaction is outside of reversal window
    '379': 'Invalid information', // Error applying surcharge
    '380': 'Invalid information', // File Upload process already running
    '386': 'Invalid information', // Required PayPal Request Method
    '387': 'Invalid information', // Invalid PayPal Request Method
    '390': 'Invalid information', // Invalid Car Rental data
    '391': 'Invalid information', // Required Car Rental data
    '392': 'Invalid information', // Required fleet products
    '393': 'Invalid information', // Fuel products not supported
    '394': 'Invalid information', // Invalid fleet product
    '395': 'Invalid information', // Cardholder Verification method required
    '402': 'Invalid information', // If not duplicate, please re-scan
    '403': 'Invalid information', // Waiting for Host Response
    '404': 'Invalid information', // Already Exists
    '410': 'Invalid information', // Invalid P2P Configuration
    '411': 'Invalid information', // Waiting for Additional Data
    '413': 'Invalid information', // Result has exceeded the record limit
    '509': 'Invalid information', // Not settled transaction
    '510': 'Invalid information', // Without record to process
    '511': 'Invalid information', // Invalid begin date
    '512': 'Invalid information', // Deposit is being processed
    '559': 'Invalid information', // The Settlement process is already scheduled
    '602': 'Invalid information', // Invalid TPI merchant type
    '603': 'Invalid information', // Invalid hierarchy
    '604': 'Invalid information', // Merchant transaction amount limit exists
    '605': 'Invalid information', // Merchant transaction count limit exists
    '606': 'Invalid information', // Recurring sale info already exists
    '607': 'Invalid information', // Invalid recurring sale token identifier
    '608': 'Invalid information', // Discount already exists
    '609': 'Invalid information', // Invalid Discount
    '610': 'Invalid information', // Recurring sale info doesn't exist
    '611': 'Invalid information', // Invoice Number already exists
    '612': 'Invalid information', // The EBPP Invoice Number is invalid
    '613': 'Invalid information', // The invoice is already paid
    '614': 'Invalid information', // Recurring sale info already exists
    '615': 'Invalid information', // Invalid recurring sale token identifier
    '616': 'Invalid information', // Recurring sale info doesn't exist
    '618': 'Invalid information', // Invalid host merchant
    '619': 'Invalid information', // Routing rules not found
    '620': 'Invalid information', // Host merchant card product already exists
    '621': 'Invalid information', // Invalid merchant routing rule
    '622': 'Invalid information', // Merchant routing rule already exists
    '623': 'Invalid information', // Invalid Priority
    '624': 'Invalid information', // User does not have a cell phone configured
    '625': 'Invalid information', // Error occurred while sending the SMS
    '626': 'Invalid information', // Invalid SecureCode Format
    '1000': 'Invalid information', // Encryption error
    '1001': 'Invalid information', // Please Hold Card
    '1002': 'Invalid information', // Re-enter
    '1003': 'Invalid information', // Please Retry
    '1004': 'Invalid information', // Invalid Operation of Card Bin
    '1005': 'Invalid information', // Call Center
    '2005': 'Invalid information', // Invalid ID Code
    '2006': 'Invalid information', // Please fill in Name on Check field
    '2007': 'Invalid information', // Please enter a valid date in Schedule Transaction Field
    '2008': 'Invalid information', // Please select a date between DATE1 and DATE2
    '2009': 'Invalid information', // Please fill in the Address Field
    '2010': 'Invalid information', // Please fill in the City Field
    '2011': 'Invalid information', // Please fill in the Zipcode Field
    '2012': 'Invalid information', // Please fill in the Phone Field
    '2013': 'Invalid information', // Please fill in the State Field
    '2014': 'Invalid information', // ID must be Numeric
    '2015': 'Invalid information', // Check Number field must be numeric
    '2016': 'Invalid information', // Application Id not found

    // For any codes not listed above, default to 'Declined'\
};

export type CenPOSResponses = 'Approved' | 'AmEx or Discover' | 'Invalid information' | 'Declined' | 'System Error';
export interface CenPOSError {
    type: CenPOSResponses;
    error_msg?: string;
    bank_code?: string;
    abi_code?: string;
}

const defaultOptions: SimpleWebPayOptions = {
    url: '',
    params: '',
    width: '100%',
    height: '400',
    success: undefined,
    cancel: undefined,
    opencomplete: undefined,
    sessionToken: true
};

// Helper function to check if a value is null or empty
function isNullOrUndefined(value: any) {
    return value === undefined || value === '' || value === null;
}

// Helper function to check if a variable is defined
function isDefined(variable: any) {
    return typeof variable !== 'undefined';
}

export class CenPOS {
    private static windowsProxyCenpos: any = null;
    private static cenposGlobalParam: any = null;
    private static onError: Function | undefined = undefined;
    static sessionExpireTime: DateTime = DateTime.fromMillis(0); //Set to 0 to expire immediately


    static checkForError(data: UseCryptoResponse): CenPOSError | null {
        if (!data || data.Result === undefined) {
            return { type: 'System Error', error_msg: 'No response from CenPOS' };
        }
        let abi_code = data.Result;
        let elavon_response = undefined;
        if (data.Result === 0) {
            return null;
        }
        if (data.hasOwnProperty('AutorizationNumber') && typeof data.AutorizationNumber === 'string' && data.AutorizationNumber.length === 2) {
            elavon_response = data.AutorizationNumber;
        }

        //Approved reponses from Elavon
        if (elavon_response === 'AA' || elavon_response === 'AC') {
            return null//{ type: 'Approved', bank_code: elavon_response };
        }
        if (elavon_response === 'AP') { //Approved for partial amount
            return { type: 'Declined', bank_code: elavon_response };
        }

        //Not approved
        if (data.CardType === 'AMEX' || data.CardType === 'MASTERCARD') {// data.CardType !== 'Credit' && data.CardType !== 'VISA' && data.CardType !== 'MASTERCARD') {
            return { type: 'AmEx or Discover', bank_code: elavon_response, abi_code: abi_code.toString() };
        }
        if (elavon_response !== undefined) {
            if (elavon_response.startsWith('N') || /^\d/.test(elavon_response)) {
                return { type: 'Declined', bank_code: elavon_response };
            }
            if (elavon_response.startsWith('R')) {
                return { type: 'Declined', bank_code: elavon_response };
            }
            if (abi_code in CenPOSResponseTypes) {
                return { type: CenPOSResponseTypes[abi_code], bank_code: elavon_response, abi_code: abi_code.toString() };
            }
        } else {
            if (!isNaN(abi_code) && abi_code > 0 && abi_code < 10000) {
                //is abi_code in CenPOSResponseTypes
                if (abi_code in CenPOSResponseTypes) {
                    return { type: CenPOSResponseTypes[abi_code], abi_code: abi_code.toString() };
                } else {
                    return { type: 'Declined', abi_code: abi_code.toString(), bank_code: elavon_response };
                }
            }
        }
        return { type: 'System Error', abi_code: abi_code.toString(), error_msg: 'Unknown error' };
    }

    static async createiframe(element: HTMLDivElement, parameters: SimpleWebPayOptions) {
        element.innerHTML = '';

        const hash = window.location.hash;
        let urlfinal = parameters.url + '?' + parameters.params;
        const cenposPayIFrameId = 'cenposPayIFrameId' + Math.floor(Math.random() * 99999 + 90000);

        const iframe = document.createElement('iframe');
        iframe.frameBorder = '0';
        iframe.id = cenposPayIFrameId;
        iframe.name = cenposPayIFrameId;
        iframe.className = 'iframecenpos';
        iframe.src = urlfinal;
        iframe.width = parameters.width;
        iframe.height = parameters.height;
        iframe.onload = () => {
            if (CenPOS.cenposGlobalParam.opencomplete) CenPOS.cenposGlobalParam.opencomplete(iframe);
        };

        element.appendChild(iframe);

        // Initialize windowsProxyCenpos here
        CenPOS.windowsProxyCenpos = new Porthole.WindowProxy(
            parameters.url.replace('default.aspx', '') + 'proxy.html?a=' + cenposPayIFrameId,
            cenposPayIFrameId
        );
        CenPOS.windowsProxyCenpos = CenPOS.windowsProxyCenpos;
        console.log('CenPOS.windowsProxyCenpos', CenPOS.windowsProxyCenpos);
        // Add or remove event listeners
        //if (CenPOS.windowsProxyCenpos) {
        CenPOS.windowsProxyCenpos.removeEventListener(CenPOS.onMessage1);
        CenPOS.windowsProxyCenpos.addEventListener(CenPOS.onMessage1);
        //}
    }

    // Function to handle iframe message events
    private static onMessage1(messageEvent: MessageEvent) {
        const data = messageEvent.data;
        // if (data?.sourceOrigin !== `https://${subdomain}.cenpos.net`) {
        //     console.log('skipping message from', data?.sourceOrigin);
        //     return;
        // }
        console.log('CenPOS.onMessage1', data);
        //console.log(CenPOS.onError, data?.msg, data?.msg?.indexOf('Error') > -1);
        if (CenPOS.onError && data?.msg && data?.msg?.indexOf('Error') > -1) {
            CenPOS.onError(data);
            return;
        }
        if (data['callbacksuccess'] && isDefined(CenPOS.cenposGlobalParam.success)) {
            CenPOS.cenposGlobalParam.success(data['transaction']);
        } else if (data['callbackcancel'] && isDefined(CenPOS.cenposGlobalParam.cancel)) {
            CenPOS.cenposGlobalParam.cancel(data['transaction']);
        }
    }

    // Function to handle iframe load event
    private static OnLoadWebpay(event) {
        if (isDefined(CenPOS.cenposGlobalParam.opencomplete)) {
            CenPOS.cenposGlobalParam.opencomplete(event);
        }
    }

    static sendPaymentDetails() {
        if (document.querySelector('.iframecenpos') && CenPOS.windowsProxyCenpos) {
            console.log('sendPaymentDetails()');
            CenPOS.windowsProxyCenpos.post({ submit: true });
        }
    }

    static async UseCrypto(): Promise<UseCryptoResponse> {
        let siteVerifyParam;
        let siteVerifyData = SiteVerify.getData();
        try {
            siteVerifyParam = await SiteVerify.getParam();
        } catch (error) {
            throw new Error(`Unable to get SiteVerifyParam: ${error}`);
        }

        const formData = new URLSearchParams();
        formData.append('verifyingpost', siteVerifyParam);
        // if (invoiceDetail) {
        //     formData.append('invoicedetail', invoiceDetail);
        // }
        for (const [key, value] of Object.entries(siteVerifyData || {})) {
            formData.append(key, value);
        }
        let jsonObj = { ...siteVerifyData, verifyingpost: siteVerifyParam }
        let endpoint = `https://${subdomain}.cenpos.net/simplewebpay/cards/api/UseCrypto/`
        console.log("Sending to CenPOS:", JSON.stringify(jsonObj))
        const response = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            //body: JSON.stringify(jsonObj),
            body: formData,
        });
        console.log("CenPOS UseCrypto response:", response)
        if (response.status != 200) {
            throw new Error(`CenPOS UseCrypto failed: ${response.status} ${response.statusText}`);
        }
        return response.json();
    }

    static async init(ParentDIV: HTMLDivElement, cust_id: SiteVerifyRequest_Cust, on_success?: Function,
        on_cancel?: Function, on_open_complete?: Function, on_error?: Function) {
        CenPOS.onError = on_error;
        ParentDIV.innerHTML = '<span style="margin-left: 10px;">Loading payment processor...</span>';
        //If loaded then get new SiteVerify parameter and call createWebpay again
        let urlParams = new URLSearchParams();
        //urlParams.append('address', '5503 Hennepin Ave Apt 107, Minneapolis, MN 55408');
        if (cust_id.customercode) {
            urlParams.append('customerCode', cust_id.customercode);
        }
        if (cust_id.email) {
            urlParams.append('email', cust_id.email);
        }
        urlParams.append('customUrlCss', PUBLIC_DOMAIN + '/CenPOS.css');
        //urlParams.append('email', 'dujavi@gmail.com');
        //urlParams.append('industry', 'eCommerce');
        //urlParams.append('invoice', '123456');
        urlParams.append('isEmail', 'false');
        urlParams.append('isCvv', USE_CVV.toString());
        urlParams.append('onlyForm', 'false');
        urlParams.append('type', transactionType);
        //urlParams.append('onlyform', 'false');

        let param = await SiteVerify.getParam();
        console.log("init(): CenPOS SiteVerify param:", param)
        //Check if res is null or empty
        if (!param) {
            console.log("CenPOS SiteVerify failed:", param)
            return
        }

        urlParams.append('verifyingpost', param);
        //urlParams.append('zipcode', '55408');
        try {
            const parameters = (CenPOS.cenposGlobalParam = {
                ...defaultOptions, ...{
                    //See jQuery plugin parameters
                    url: `https://${subdomain}.cenpos.net/simplewebpay/cards/`,
                    params: '&' + urlParams.toString(),
                    width: '559',
                    height: '390',
                    success: on_success,
                    cancel: on_cancel,
                    opencomplete: on_open_complete, //opencomplete,
                    sessionToken: true
                }
            });

            if (isNullOrUndefined(parameters.url)) {
                throw new Error('The Url Parameter is empty');
            }

            if (CenPOS.cenposGlobalParam.success) CenPOS.cenposGlobalParam.params += '&callbacksuccess=true';
            if (CenPOS.cenposGlobalParam.cancel) CenPOS.cenposGlobalParam.params += '&callbackcancel=true';
            if (CenPOS.cenposGlobalParam.sessionToken) CenPOS.cenposGlobalParam.params += '&sessiontoken=true';

            CenPOS.createiframe(ParentDIV, parameters);

            // Set up postMessage listener
            window.addEventListener('message', CenPOS.onMessage1);
            CenPOS.sessionExpireTime = DateTime.now().plus({ minutes: 15 });
        } catch (exception) {
            alert(`Exception: ${exception.message || exception.description || exception}`);
        }
    }
}

function ensureValidSession() {
    if (CenPOS.sessionExpireTime < DateTime.now()) {
        //Reload the component
        CenPOSHandler.loadCenPOSiFrame();
    }
}


export class CenPOSHandler {
    static successObj: OnSuccessObj | null = null;
    static lastSuccessObj: OnSuccessObj | null = null;
    private static paymentError: null | { msg: string } = null;
    private static loading = false;
    private static loaded = false;

    public static async submit() {
        CenPOS.sendPaymentDetails();
        return await CenPOSHandler.getResponse();
    }
    public static onSuccess(response: OnSuccessObj) {
        CenPOSHandler.successObj = response;
        CenPOSHandler.lastSuccessObj = response;
    }
    public static async getResponse() {
        // Wait until response is received and then return it
        // When it is returned, reset its value to null
        while (CenPOSHandler.successObj === null) {
            const timeout = Date.now() + 15000;
            while (CenPOSHandler.successObj === null && Date.now() < timeout) {
                await new Promise((r) => setTimeout(r, 100));
            }
            if (CenPOSHandler.successObj === null) {
                throw new Error('Timed out waiting for token');
            }
            if (CenPOSHandler.paymentError) {
                const error = CenPOSHandler.paymentError;
                CenPOSHandler.paymentError = null;
                throw new Error('Payment error: ' + error.msg);
            }
        }
        const response = CenPOSHandler.successObj;
        return response;
    }
    public static onError(response: any) {
        console.log('onError', response);
        CenPOSHandler.paymentError = response;
        if (response?.Result === -1) {
            if (response?.Message == 'The session data is expired, please trying again with another') {
                KTIForm.error_modal.Show('The session has expired please re-enter card details.');
            }
        } else if (response.hasOwnProperty('msg') && response.msg == 'Error in Form') {
            console.log('showInvalidInformationError()', response);
            _showInvalidInformationError();
        } else if (response.hasOwnProperty('msg')) {
            console.log('showInvalidInformationError()', response);
            _showSystemError('A45');
        }
        //$errorModal.Show('There was an error with your credit card details. Please check try again.');

        //Reload the frame
        CenPOSHandler.loadCenPOSiFrame();
    }
    public static OnCancel(response: any) {
        console.log('OnCancel', response);
        CenPOSHandler.paymentError = response;
    }
    public static onOpen(response: any) {
        console.log('onOpen', response);
        CenPOSHandler.loading = false;
        CenPOSHandler.loaded = true;
        onLoaded();
        //Hide loading div
    }
    public static loadCenPOSiFrame(_customer_id?: string, email?: string, force?: boolean) {
        if (browser) {
            if (!force) {
                if (_customer_id) {
                    if (cust_id.customercode == _customer_id) {
                        console.log('Customer ID is unchanged, not reloading CenPOS iframe');
                        return;
                    }
                    cust_id.customercode = _customer_id;
                    cust_id.email = '';
                } else if (email) {
                    if (cust_id.email == email) {
                        console.log('Email is unchanged, not reloading CenPOS iframe');
                        return;
                    }
                    cust_id.email = email;
                    //If loaded/loading and cust_id.customercode is not set, then load with email
                    if ((CenPOSHandler.loaded || CenPOSHandler.loading) && cust_id.customercode) {
                        return;
                    }
                } else if (!cust_id.customercode && !cust_id.email) {
                    console.log('No customer ID or email available, not loading CenPOS iframe');
                    CenPOSHandler.loading = false;
                    CenPOSHandler.loaded = false;
                    onLoading();
                    return;
                }
            }
            if (CenPOSHandler.loading) {
                //cancel loading and reload
                console.warn('CenPOS is already loading, cancelling and reloading');
            }
            CenPOSHandler.loading = true;
            onLoading();
            CenPOS.init(
                _CenPOSEl,
                cust_id,
                CenPOSHandler.onSuccess,
                CenPOSHandler.OnCancel,
                CenPOSHandler.onOpen,
                CenPOSHandler.onError
            );
            //Set timeout fo 15 minutes and see if session has expired, if it has reload the component
            setTimeout(ensureValidSession, 900000);
        }
    }
}

export class SiteVerify {
    // data needed for SiteVerifyRequest
    static invoicenumber: string = '';
    static email: string = '';
    static type: string = 'Sale';
    static city: string = '';
    static state: string = '';
    static zipcode: string = '';
    static verifyingpost_token: string = '';
    static token_used = true;
    static getData(): SiteVerifyRequest {
        let siteVerifyData: SiteVerifyRequest = {
            invoicenumber: cust_id.customercode || DateTime.now().toFormat('yyMMddHHmmss'),
            type: transactionType ?? 'Sale',
            type3d: 'FunctionAuto',
            cvv: USE_CVV.toString(),
            ...address
        };
        if (amount) {
            siteVerifyData.amount = amount;
        }
        if (cust_id.customercode) {
            siteVerifyData.customercode = cust_id.customercode;
        } else if (cust_id.email) {
            siteVerifyData.email = cust_id.email;
        }
        console.log('getData.successObj', CenPOSHandler.successObj);
        if (CenPOSHandler.successObj?.RecurringSaleTokenId) {
            siteVerifyData.tokenid = CenPOSHandler.successObj.RecurringSaleTokenId;
        }
        return siteVerifyData;
    }
    static async getParam(): Promise<string> {
        if (!cust_id?.customercode && !cust_id?.email) {
            throw new Error('getSiteVerifyParam(): Customer code or email is required');
        }

        if (this.token_used) {
            console.log('Getting a new SiteVerifyToken');

            let res = await REST.GET('SiteVerifyParam', SiteVerify.getData());
            // let res = await fetch(`/api/SiteVerifyParam?${params.toString()}`, {
            //     method: 'GET' // 'GET' is the default method, so this line is optional
            // });
            //Check if response is 200
            if (!res.ok) {
                console.log('CenPOS SiteVerify failed:', res.status, res.statusText);
                return '';
                //throw new Error(`CenPOS SiteVerify failed: ${res.status} ${res.statusText}`);
            }
            let data = await res.json();
            if (data?.duplicate_entry) {
                //Get dealer selector component
                // let erase = get(eraseDealer)
                // erase()
                // let modal = get(errorModal)
                // modal.SetTmpTitle('Dealer has already ordered');
                // modal.Show("We've already received a payment for this dealer, if you'd like to adjust your order please contact Customer Service @ 1-800-328-8900. Thank you!")
            }
            //handle if data isn't json
            //console.log("data", data)
            if (data?.SiteVerifyParam) {
                if (CenPOSHandler.successObj?.RecurringSaleTokenId) {
                    console.log("Consuming token")
                    CenPOSHandler.successObj = null;
                }
                return data.SiteVerifyParam;
            } else {
                throw new Error(`CenPOS SiteVerify failed (param missing): ${JSON.stringify(data)}`);
            }
        } else {
            console.log('Using existing SiteVerifyToken');
            return this.verifyingpost_token;
        }
    }
}